'use client'

import { CheckIcon, CopyIcon } from 'lucide-react'
import * as React from 'react'

import { Button } from '../button'

export interface CopyButtonProps extends React.ComponentProps<typeof Button> {
  value: string
  onCopyError?: (error: unknown) => void
}

const CopyButton = React.forwardRef<HTMLButtonElement, CopyButtonProps>(
  ({ value, onCopyError, onClick, ...props }, ref) => {
    const [hasCopied, setHasCopied] = React.useState(false)
    const resetTimer = React.useRef<ReturnType<typeof setTimeout>>(undefined)

    React.useEffect(() => () => clearTimeout(resetTimer.current), [])

    const handleCopy = async (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event)
      try {
        await navigator.clipboard.writeText(value)
        setHasCopied(true)
        clearTimeout(resetTimer.current)
        resetTimer.current = setTimeout(() => setHasCopied(false), 2000)
      } catch (error) {
        onCopyError?.(error)
      }
    }

    return (
      <Button
        ref={ref}
        type='button'
        size='sm'
        variant='ghost'
        className='text-muted-foreground'
        onClick={handleCopy}
        {...props}
      >
        {hasCopied ? <CheckIcon className='h-4 w-4' /> : <CopyIcon className='h-4 w-4' />}
        <span className='sr-only'>Copy</span>
      </Button>
    )
  },
)
CopyButton.displayName = 'CopyButton'

export { CopyButton }
